import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { 
  FaClock, 
  FaCheckCircle, 
  FaGlobeAmericas, 
  FaBuilding, 
  FaUserCheck, 
  FaPassport, 
  FaPlaneDeparture, 
  FaIdCard,
  FaFileAlt,
  FaSignature,
  FaUniversity,
  FaBriefcase,
  FaShieldAlt,
  FaMapMarkerAlt
} from "react-icons/fa";
import "../Styles/LatviaTRP.css";
import Toast from "./Toast";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

export default function LatviaTRP() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/consultationForm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: "Latvia TRP",
          source: "Latvia TRP page",
          inquiryType: "Latvia TRP",
          description: `Latvia TRP Program Enquiry. Message: ${formData.message}`
        })
      });
      const result = await response.json();
      if (result.status === "success") {
        setToast({ message: "Enquiry submitted successfully! Our team will contact you.", type: "success" });
        setFormData({ name: "", email: "", phone: "", message: "" });
        setSubmitted(true);
      } else {
        setToast({ message: "Submission failed. Please try again.", type: "error" });
      }
    } catch (error) {
      setToast({ message: "Network error. Please try again.", type: "error" });
    }
    setTimeout(() => setToast(null), 4000);
  };

  const steps = [
    {
      num: 1,
      icon: <FaFileAlt />,
      title: "Document Collection",
      desc: "Gathering and compiling all necessary personal documents, background checks, and certifications."
    },
    {
      num: 2,
      icon: <FaSignature />,
      title: "Digital Signature Creation",
      desc: "Setting up secure Latvian e-signatures for paperless document execution and submission."
    },
    {
      num: 3,
      icon: <FaBuilding />,
      title: "Company Formation",
      desc: "Incorporating and registering your company in the official Latvian Register of Enterprises."
    },
    {
      num: 4,
      icon: <FaUniversity />,
      title: "Latvian Bank Account Opening",
      desc: "Opening a secure corporate bank account to manage your business investments and assets."
    },
    {
      num: 5,
      icon: <FaPassport />,
      title: "Application for TRP",
      desc: "Submitting your Temporary Residence Permit dossier to the Latvian Office of Citizenship & Migration Affairs (OCMA)."
    },
    {
      num: 6,
      icon: <FaUserCheck />,
      title: "Positive Decision on TRP",
      desc: "Official processing is completed and you receive your formal TRP approval letter."
    },
    {
      num: 7,
      icon: <FaPlaneDeparture />,
      title: "Apply for D Visa",
      desc: "Applying for the Schengen National Long-Stay (D) Visa at the embassy to facilitate legal entry."
    },
    {
      num: 8,
      icon: <FaMapMarkerAlt />,
      title: "Enter Latvia & Biometric Scan",
      desc: "Traveling to Riga, Latvia to provide biometrics (fingerprints and photo) at the OCMA office."
    },
    {
      num: 9,
      icon: <FaIdCard />,
      title: "Collect TRP Card",
      desc: "Picking up your physical biometric TRP card, granting you full Schengen residence status."
    }
  ];

  return (
    <div className="latvia-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Helmet>
        <title>Latvia Temporary Residence Permit (TRP) Program | LIBA Migration</title>
        <meta name="description" content="Get your Latvia Temporary Residence Permit (TRP) in 45-60 days. Includes company formation, bank account opening, and Schengen access. Contact LIBA today!" />
        <link rel="canonical" href="https://veabroad.com/latvia-trp" />
        <meta property="og:title" content="Latvia Temporary Residence Permit (TRP) Program | LIBA Migration" />
        <meta property="og:description" content="Get your Latvia Temporary Residence Permit (TRP) in 45-60 days. Includes company formation, bank account opening, and Schengen access. Contact LIBA today!" />
        <meta property="og:url" content="https://veabroad.com/latvia-trp" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Latvia Temporary Residence Permit (TRP) Program",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Liba Migration Services",
              "url": "https://veabroad.com/LibaMigration",
              "parentOrganization": {
                "@type": "EducationalOrganization",
                "name": "Vidya Education Abroad",
                "url": "https://veabroad.com/"
              }
            },
            "areaServed": "IN",
            "description": "9-step business pathway to obtain Latvian Temporary Residence Permit with Schengen access and corporate setup support."
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="latvia-hero">
        <div className="latvia-hero-overlay" />
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: '-5%', left: '10%', width: '300px', height: '300px' }} />
          <div className="va-blob va-blob-teal" style={{ bottom: '-10%', right: '5%', width: '380px', height: '380px' }} />
          <div className="va-blob va-blob-navy" style={{ top: '40%', right: '35%', width: '240px', height: '240px' }} />
        </div>
        <div className="container latvia-hero-inner">
          <Reveal as="div" direction="left" className="latvia-hero-text text-start">
            <div className="latvia-hero-badge">🌍 LIBA Migration Services</div>
            <h1 className="text-white">Latvian <span className="va-shine-text">TRP</span> Program</h1>
            <p className="text-white opacity-95">
              Secure your Schengen residency card through business investments. LIBA Migration Services handles everything from company incorporation and bank accounts to visa approvals.
            </p>
            <div className="latvia-hero-features">
              <div className="latvia-hero-feature text-white"><FaClock /> Fast Process: 45 - 60 Days</div>
              <div className="latvia-hero-feature text-white"><FaBuilding /> Includes Company Formation</div>
              <div className="latvia-hero-feature text-white"><FaUniversity /> Corporate Bank Account Setup</div>
            </div>
          </Reveal>
          <Reveal as="div" direction="right" delay={0.15} className="latvia-hero-form-wrap">
            {submitted ? (
              <div className="latvia-form-success">
                <div className="latvia-form-success-icon">✅</div>
                <h3>Enquiry Received!</h3>
                <p>One of our certified immigration consultants will reach out to you within 24 hours.</p>
              </div>
            ) : (
              <form className="latvia-glass-form text-start" onSubmit={handleSubmit}>
                <h3>Start Your Assessment</h3>
                <p>Consult with our Latvia immigration specialists today.</p>
                <div className="latvia-form-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    placeholder="Enter your name" 
                    required 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})} 
                  />
                </div>
                <div className="latvia-form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    placeholder="your.email@example.com" 
                    required 
                    value={formData.email} 
                    onChange={e => setFormData({...formData, email: e.target.value})} 
                  />
                </div>
                <div className="latvia-form-group">
                  <label>Mobile Number</label>
                  <input 
                    type="tel" 
                    placeholder="+91 (555) 000-0000" 
                    required 
                    value={formData.phone} 
                    onChange={e => setFormData({...formData, phone: e.target.value})} 
                  />
                </div>
                <div className="latvia-form-group">
                  <label>Your Message (Optional)</label>
                  <textarea 
                    placeholder="Tell us about your background or requirements..." 
                    rows="2" 
                    value={formData.message} 
                    onChange={e => setFormData({...formData, message: e.target.value})} 
                  />
                </div>
                <MagneticButton as="span" className="w-100">
                  <button type="submit" className="latvia-form-submit va-shimmer-btn">Submit Enquiry</button>
                </MagneticButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE LATVIA */}
      <section className="latvia-section latvia-light-bg">
        <div className="container">
          <Reveal as="h2">Why Choose Latvia?</Reveal>
          <div className="latvia-section-underline"></div>
          <RevealGroup as="div" className="latvia-benefits-grid" stagger={0.12}>
            <RevealItem as="div" className="latvia-benefit-card va-hover-lift">
              <div className="latvia-benefit-icon va-float"><FaGlobeAmericas /></div>
              <h3>Schengen Access</h3>
              <p>Enjoy absolute visa-free travel and movement within the 27 Schengen area nations for up to 90 days per half-year.</p>
            </RevealItem>
            <RevealItem as="div" className="latvia-benefit-card va-hover-lift">
              <div className="latvia-benefit-icon va-float" style={{ animationDelay: '0.3s' }}><FaBriefcase /></div>
              <h3>Strategic European Hub</h3>
              <p>Position your business in a gateway location between Northern, Western, and Eastern Europe with a favorable tax regime.</p>
            </RevealItem>
            <RevealItem as="div" className="latvia-benefit-card va-hover-lift">
              <div className="latvia-benefit-icon va-float" style={{ animationDelay: '0.6s' }}><FaShieldAlt /></div>
              <h3>High Standard of Living</h3>
              <p>Experience a peaceful, safe, clean, and welcoming environment with excellent infrastructure and public facilities.</p>
            </RevealItem>
            <RevealItem as="div" className="latvia-benefit-card va-hover-lift">
              <div className="latvia-benefit-icon va-float" style={{ animationDelay: '0.9s' }}><FaCheckCircle /></div>
              <h3>LIBA Expert Guidance</h3>
              <p>Get comprehensive corporate, legal, and administrative assistance at every single stage of your application.</p>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* APPLICATION PROCESS STEPS */}
      <section className="latvia-timeline-section">
        <div className="container">
          <Reveal as="h2">Fast & Simple 9-Step Process</Reveal>
          <div className="latvia-section-underline"></div>
          <RevealGroup as="div" className="latvia-timeline" stagger={0.08}>
            {steps.map((s, idx) => (
              <RevealItem as="div" key={idx} direction={idx % 2 === 0 ? "left" : "right"} className="latvia-timeline-item">
                <div className="latvia-timeline-number">{s.num}</div>
                <div className="latvia-timeline-content">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span style={{ fontSize: '1.2rem', color: idx % 2 === 0 ? '#9e2a2b' : '#36948c' }}>{s.icon}</span>
                    <h3 className="mb-0">{s.title}</h3>
                  </div>
                  <p>{s.desc}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="latvia-cta">
        <div className="container">
          <Reveal direction="zoom">
            <h2>Begin Your Journey to Latvian Residency</h2>
            <p>
              Speak directly with our LIBA Migration team to organize document collection, company incorporation, and secure your Schengen residence.
            </p>
            <MagneticButton as="span">
              <Link to="/consultationForm" className="latvia-cta-btn">
                Get Free Consultation Now
              </Link>
            </MagneticButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
